import React, { useState } from "react";
import { Linkedin, Facebook, Link2, Check } from "lucide-react";

// Plain share links rather than the networks' own widgets: those pull in
// third-party JavaScript, set cookies before anyone clicks, and this page
// already carries GA4, the Meta Pixel and a chat widget. An <a> to the same
// intent URL costs nothing and cannot track anyone who does not use it.
const BlogShare = ({ url, title }) => {
  const [copied, setCopied] = useState(false);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const targets = [
    {
      name: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      Icon: Linkedin,
      className: "bg-[#0a66c2] hover:bg-[#08508f]",
    },
    {
      name: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      Icon: Facebook,
      className: "bg-[#1877f2] hover:bg-[#0f5bc0]",
    },
    {
      name: "WhatsApp",
      // The audience for these posts is Indian homeowners; WhatsApp is where
      // this actually gets forwarded.
      href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      Icon: null,
      className: "bg-[#25d366] hover:bg-[#1da851]",
    },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is refused without a user gesture in a secure context, and
      // over plain http. Nothing useful to say, so leave the button as-is.
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-[13px] font-medium text-[#6b7280] mr-1">Share</span>

      {targets.map(({ name, href, Icon, className }) => (
        <a
          key={name}
          href={href}
          target="_blank"
          // noreferrer alongside noopener: these open a new tab, which is
          // exactly the case where the opener reference matters.
          rel="noopener noreferrer"
          aria-label={`Share on ${name}`}
          title={`Share on ${name}`}
          className={`grid h-9 w-9 place-items-center rounded-full text-white transition-colors ${className}`}
        >
          {Icon ? (
            <Icon size={16} aria-hidden="true" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 004.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0012.04 2zm5.8 14.17c-.25.69-1.45 1.32-1.99 1.36-.53.04-1.03.23-3.47-.72-2.93-1.15-4.8-4.13-4.94-4.32-.15-.2-1.19-1.58-1.19-3.02s.76-2.14 1.03-2.44c.27-.3.58-.37.78-.37h.56c.18 0 .42-.7.66.5.25.58.83 2.02.9 2.17.07.15.12.32.02.52-.1.2-.15.32-.3.5l-.44.51c-.15.15-.3.31-.13.61.17.3.76 1.25 1.63 2.02 1.12 1 2.06 1.31 2.36 1.46.3.15.47.13.64-.08.17-.2.74-.86.94-1.16.2-.3.4-.25.66-.15.27.1 1.7.8 1.99.95.3.15.5.22.57.35.07.12.07.72-.18 1.41z" />
            </svg>
          )}
        </a>
      ))}

      <button
        type="button"
        onClick={copy}
        aria-label={copied ? "Link copied" : "Copy link"}
        title={copied ? "Link copied" : "Copy link"}
        className="grid h-9 w-9 place-items-center rounded-full bg-[#e8efe8] text-[#4b5563] transition-colors hover:bg-[#d8e6d8] hover:text-[#117816]"
      >
        {copied ? <Check size={16} /> : <Link2 size={16} />}
      </button>
    </div>
  );
};

export default BlogShare;
